const { fetchProductsBatched } = window.storefrontApi.createNodesChunkedProductsService({
  query: window.storefrontApi.PRODUCT_CARD_QUERY,
  chunkSize: 25,
  flushDelay: 30,
});

// --- PRODUCT CARD VIEW MODEL ---

const PRODUCT_SELECTORS = {
  IMAGE: '.image-button__image',
  LINK: '.image-button__link',
  TITLE: '[data-replaceable-card-title]',
  PRICE: '[data-replaceable-card-price]',
  PRICE_LABEL: '[data-replaceable-card-price-label]',
  LOGO: '[data-replaceable-card-logo]',
  HIGHLIGHTS: '[data-replaceable-card-highlights]',
  PRODUCT_LABEL: '[data-replaceable-card-product-label]',
  SWATCH_LIST: '.c-product-option__list',
  SWATCH_TEMPLATE: '.c-product-option__list .o-style-option__list-item',
  SWATCH_CLASS: 'color-selector',
  INFO_FOOTER_CLASS: 'c-product-tile__footer',
  COLOR_VALUE_WRAPPER: 'o-style-option__list-item',
};

const { generateSrcSet, cardHelpers } = window.utilities;

class ProductCardViewModel {
  constructor(scope) {
    this.$scope = scope;
    this.vm = this.getViewModel();
  }

  getViewModel() {
    return {
      $image: this.$scope.querySelector(PRODUCT_SELECTORS.IMAGE),
      $link: this.$scope.querySelector(PRODUCT_SELECTORS.LINK),
      $title: this.$scope.querySelector(PRODUCT_SELECTORS.TITLE),
      $price: this.$scope.querySelector(PRODUCT_SELECTORS.PRICE),
      $priceLabel: this.$scope.querySelector(PRODUCT_SELECTORS.PRICE_LABEL),
      $logo: this.$scope.querySelector(PRODUCT_SELECTORS.LOGO),
      $highlights: this.$scope.querySelector(PRODUCT_SELECTORS.HIGHLIGHTS),
      $productLabel: this.$scope.querySelector(PRODUCT_SELECTORS.PRODUCT_LABEL),
      $swatchList: this.$scope.querySelector(PRODUCT_SELECTORS.SWATCH_LIST),
      $swatchTemplate: this.$scope.querySelector(PRODUCT_SELECTORS.SWATCH_TEMPLATE),
      $infoFooter: this.$scope.querySelector(`.${PRODUCT_SELECTORS.INFO_FOOTER_CLASS}`),
    };
  }
}

class ProductCardDOMPatcher {
  constructor(scope) {
    this.scope = scope;
    this.viewModel = scope.viewModel.vm;
    this.priceLabel = '';
  }

  render(data) {
    this.renderTitle(data);
    this.renderImage(data);
    this.renderPrice(data);
    this.renderLogo(data);
    this.renderHighlights(data);
    this.renderSwatches(data);
    this.renderProductLabel(data);
    this.setLink(data);
  }

  setLink(data) {
    const { $link } = this.viewModel;

    $link.href = data.url;
  }

  renderImage(data) {
    const { $image } = this.viewModel;

    const selectedColor = data?.swatches?.length
      ? data.swatches.find((item) => item.isChecked) || data.swatches[0]
      : '';
    const color = selectedColor ? selectedColor.label : '';

    if ($image && data.image) {
      const widths = [165, 360, 533, 720, 940, 1000];
      const sizes =
        '(min-width: 1440px) 1000vw, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';

      const { src, srcset, sizes: sizeAttr } = window.utilities.generateSrcSet(data.image.src, widths, '533', sizes);

      $image.src = src;
      $image.srcset = srcset;
      $image.sizes = sizeAttr;
      $image.alt = data.image.alt || this.viewModel.$title?.innerHTML + ' - ' + color || '';
      $image.width = data.image.width || '';
      $image.height = data.image.height || '';
      $image.loading = 'lazy';
    }
  }

  renderTitle(data) {
    const { $title } = this.viewModel;
    if (!$title || !data?.title) return;

    let text = data.title;
    const productType = (data?.productType || '').toLowerCase();

    if (data.flags.silhouette) {
      text += ` - ${data.flags.silhouette}`;
    }

    if (productType.toLowerCase() === 'footwear' && data.flags.heelType) {
      text += `, ${data.flags.heelType}`;
    }

    $title.textContent = text;
  }

  renderPrice(data) {
    const { $price } = this.viewModel;
    if (!$price) return;

    let promoPriceMessage;
    switch (window.localeCode.toLowerCase()) {
      case 'fr-ca':
        promoPriceMessage = data.flags?.promotionalPriceMessageCAFR;
        break;
      case 'en-ca':
        promoPriceMessage = data.flags?.promotionalPriceMessageCAEN;
        break;
      default:
        promoPriceMessage = data.flags?.promotionalPriceMessageUSEN;
    }

    const country = Shopify.country || 'US';
    const isloyaltyEarlyBuy =
      data.flags?.[`loyaltyEarlyBuy${country.toUpperCase()}`] === true ||
      data.flags?.[`loyaltyEarlyBuy${country.toUpperCase()}`] === 'true';

    const loyaltyEarlyBuyEndDate = data.flags?.[`loyaltyEarlyBuyEndDate${Shopify.country}`];
    const loyaltyEarlyBuyEndDateValue = loyaltyEarlyBuyEndDate
      ? new Date(loyaltyEarlyBuyEndDate).getTime() / 1000
      : null;
    const isloyaltyEarlyBuyValid = isloyaltyEarlyBuy && loyaltyEarlyBuyEndDateValue > Date.now() / 1000;

    const crewPrice = parseFloat(data.flags?.[`crewExclusivePrice${country.toUpperCase()}`] || 0);

    const hasCrewPrice = crewPrice > 0;
    const t = window.productTileStrings || {};
    const currency = Shopify.currency.active;

    const isLoggedIn = !!window.customer?.id;
    const isLoyal = window.customer?.loyaltyStatus === 'active';

    const formattedCrewPrice = window.utilities.formatPrice(currency, Math.round(crewPrice));

    let mainPrice = window.utilities.formatPrice(currency, data.price);
    let comparePrice = window.utilities.formatPrice(currency, data.compareAtPrice) || '';
    let label = '';
    let labelColorClass = 'c-product-text-color--loyalty';
    let priceClass = '';

    if (promoPriceMessage) {
      label = promoPriceMessage;
      labelColorClass = 'c-product-text-color--urgency';

      if (comparePrice) priceClass = 'c-product-price--has-discount';
    } else if (isloyaltyEarlyBuyValid) {
      comparePrice = '';
      priceClass = 'c-product-price--crew-only';
    } else if (hasCrewPrice) {
      if (isLoggedIn && isLoyal) {
        label = t.membersOnlyPrice;
        mainPrice = formattedCrewPrice;
        comparePrice = window.utilities.formatPrice(currency, data.price);
        priceClass = 'c-product-price--crew-main';
      } else {
        label = t.membersOnlyPrice;
        mainPrice = window.utilities.formatPrice(currency, data.price);
        comparePrice = formattedCrewPrice;
        priceClass = 'c-product-price--guest-view';
      }
    } else if (comparePrice) {
      priceClass = 'c-product-price--has-discount';
    }

    $price.innerHTML = `
      
      <div class="c-product-price c-product-tile__product-price ${priceClass}">
        ${
          comparePrice
            ? `<span class="visually-hidden visually-hidden--inline">
                ${t.sale_price} &nbsp;
              </span>`
            : ''
        }
        <span class="c-product-price__formatted-price fixed-utility-m">${mainPrice}</span>
        ${
          comparePrice
            ? `<span class="visually-hidden visually-hidden--inline">
                &nbsp; ${t.regular_price} &nbsp;
                </span> 
                <span class="c-product-price__formatted-price c-product-price__formatted-price--discounted fixed-utility-s">${comparePrice}</span>`
            : ''
        }
      </div>
    `;

    this.priceLabel = label;
    this.renderPriceLabel(label, labelColorClass);
  }

  renderLogo(data) {
    const { $logo } = this.viewModel;

    if (!this.logosMap) {
      const scriptTag = this.scope.querySelector('[data-product-logo-json]');
      this.logosMap = window.utilities.safeParseJSON(scriptTag?.textContent, {});
    }

    $logo.innerHTML = '';

    if (!$logo || !data.logoBadge) return;

    const logo = this.logosMap?.[data.logoBadge];

    if (!logo) return;

    const orientation = logo.aspectRatio < 1 ? 'vertical' : 'horizontal';
    $logo.className = `c-product-tile__logo-badge__wrapper c-product-tile__logo-badge--${orientation}`;

    $logo.innerHTML = `
      <img
        src="${logo.url}"
        alt="${logo.alt}"
        class="c-product-tile__logo-badge"
        width="86"
        loading="lazy"
      >
    `;
  }

  renderHighlights(data) {
    const { $highlights } = this.viewModel;

    if (!this.highlightMap) {
      const scriptTag = this.scope.querySelector('[data-product-highlights-json]');
      this.highlightMap = window.utilities.safeParseJSON(scriptTag?.textContent, {});
    }

    $highlights.innerHTML = '';

    if (!$highlights || !data.highlights?.length) return;

    const filteredHighlights = data.highlights.filter((h) => {
      const key = h?.label;
      const highlight = this.highlightMap?.[key];
      try {
        if (!highlight.show_only_on_pdp) return h;
      } catch {
        /* no-op */
      }
    });

    const key = filteredHighlights[0]?.label;
    const highlight = this.highlightMap?.[key];
    if (!highlight) return;

    $highlights.innerHTML = `
      <ul class="c-highlights c-highlights--top">
        <li class="c-highlights__highlight">
          <div class="c-highlights__highlights_item">
            <img src="${highlight.icon}" aria-hidden="true" alt="" class="c-highlights__highlight-icon" width="16" height="16" loading="lazy">
            <span class="c-highlights__highlight-value fixed-utility-s">${highlight.label}</span>
          </div>
        </li>
      </ul>`;
  }

  renderSwatches(data) {
    const { $swatchList, $swatchTemplate } = this.viewModel;
    if ($swatchList && $swatchTemplate && data.swatches?.length) {
      $swatchList.innerHTML = '';

      data.swatches.forEach((swatch) => {
        const clone = $swatchTemplate.cloneNode(true);
        const button = clone.querySelector('button');
        const img = clone.querySelector('img');

        if (button) {
          button.setAttribute('data-product-id', swatch.productID);
          button.setAttribute('data-color-label', swatch.label);
          button.setAttribute('data-swatch-image', swatch.image);

          if (swatch.isChecked) button.classList.add('is-checked');
        }

        if (img) {
          img.src = `${swatch.image}&height=64&width=64`;
          img.alt = swatch.label;
          img.loading = 'lazy';
        }

        if (swatch.isChecked) {
          $swatchList.prepend(clone);
        } else {
          $swatchList.appendChild(clone);
        }
      });
    }
  }

  renderProductLabel(data) {
    const { $productLabel } = this.viewModel;
    if (!$productLabel) return;

    // Only 1 label per tile. If price label rendered - remove Context label
    if (this.priceLabel) {
      $productLabel.innerHTML = '';
      return;
    }

    const hasTag = (tag) => tags.includes(tag.toLowerCase());

    const isLowStock = cardHelpers.isLowStock(data);

    const availableForSale = data.availableForSale ?? false;
    const tags = Array.isArray(data.tags) ? data.tags.map((t) => t.toLowerCase()) : [];

    const publishDateField = data.flags[`publishDate${window.Shopify.country}`];
    const publishDate = publishDateField ? new Date(publishDateField) : null;
    const daysSincePublish = publishDate ? (new Date() - publishDate) / (1000 * 60 * 60 * 24) : null;

    const onlineExclusive = data.flags?.onlineExclusive === true || data.flags?.onlineExclusive === 'true';
    const isPreOrder =
      data.flags[`preOrder${window.Shopify.country}`] === true ||
      data.flags[`preOrder${window.Shopify.country}`] === 'true';
    const isCommingSoon =
      data.flags[`comingSoon${window.Shopify.country}`] === true ||
      data.flags[`comingSoon${window.Shopify.country}`] === 'true';
    const isloyaltyEarlyBuy =
      data.flags?.[`loyaltyEarlyBuy${Shopify.country}`] === true ||
      data.flags?.[`loyaltyEarlyBuy${Shopify.country}`] === 'true';
    const loyaltyEarlyBuyEndDate = data.flags?.[`loyaltyEarlyBuyEndDate${Shopify.country}`];
    const loyaltyEarlyBuyEndDateValue = loyaltyEarlyBuyEndDate
      ? new Date(loyaltyEarlyBuyEndDate).getTime() / 1000
      : null;
    const isloyaltyEarlyBuyValid = isloyaltyEarlyBuy && loyaltyEarlyBuyEndDateValue > Date.now() / 1000;

    let promotionalMessage;
    switch (window.localeCode.toLowerCase()) {
      case 'fr-ca':
        promotionalMessage = data.flags?.promotionalMessageCAFR;
        break;
      case 'en-ca':
        promotionalMessage = data.flags?.promotionalMessageCAEN;
        break;
      default:
        promotionalMessage = data.flags?.promotionalMessageUSEN;
    }

    const nonReturnable = !!data.flags?.nonReturnableType;

    const isNotSpecialTagged = !isPreOrder && !isCommingSoon && !hasTag('excludedfrompromotions');
    const isTrulyNeutral = isNotSpecialTagged && !isloyaltyEarlyBuyValid && !onlineExclusive && !promotionalMessage;

    const t = window.productTileStrings || {};

    let label = '';
    let labelClass = '';

    // 1. Sold Out
    if (!availableForSale && !hasTag('notify') && !isCommingSoon && !isPreOrder) {
      label = t.soldOut;
      labelClass = 'c-product-text-color--urgency';
      this.viewModel.$price.remove(); // Remove price for sold out item
    }

    // 2. Preorder
    else if (availableForSale && isPreOrder) {
      label = t.preorder;
      labelClass = 'c-product-text-color--regular';
    }

    // 3. Early Access
    else if (availableForSale && !isPreOrder && isloyaltyEarlyBuyValid) {
      label = t.earlyAccess;
      labelClass = 'c-product-text-color--loyalty';
    }

    // 4. Coming Soon
    else if (availableForSale && !isPreOrder && !isloyaltyEarlyBuyValid && isCommingSoon) {
      label = t.comingSoon;
      labelClass = 'c-product-text-color--loyalty';
    }

    // 5. Excluded from Promotions
    else if (
      availableForSale &&
      !isPreOrder &&
      !isCommingSoon &&
      !isloyaltyEarlyBuyValid &&
      hasTag('excludedfrompromotions')
    ) {
      label = t.noPromo;
      labelClass = 'c-product-text-color--regular';
    }

    // 6. Online Exclusive
    else if (availableForSale && isNotSpecialTagged && !isloyaltyEarlyBuyValid && onlineExclusive) {
      label = t.onlineExclusive;
      labelClass = 'c-product-text-color--regular';
    }

    // 7. Promotional Messaging
    else if (
      availableForSale &&
      isNotSpecialTagged &&
      !isloyaltyEarlyBuyValid &&
      !onlineExclusive &&
      promotionalMessage
    ) {
      label = promotionalMessage;
      labelClass = 'c-product-text-color--regular';
    }

    // 8. New
    else if (availableForSale && isTrulyNeutral && daysSincePublish !== null && daysSincePublish <= 60) {
      label = t.new;
      labelClass = 'c-product-text-color--regular';
    }

    // 9. Low Stock
    else if (availableForSale && isTrulyNeutral && daysSincePublish !== null && daysSincePublish > 60 && isLowStock) {
      label = t.lowStock;
      labelClass = 'c-product-text-color--urgency';
    }

    // 10. Last Chance
    else if (
      availableForSale &&
      isTrulyNeutral &&
      daysSincePublish !== null &&
      daysSincePublish > 60 &&
      !isLowStock &&
      nonReturnable
    ) {
      label = t.finalSale;
      labelClass = 'c-product-text-color--urgency';
    }

    $productLabel.innerHTML = label
      ? `<span class="c-product-tile__product-label-content fixed-utility-s ${labelClass}">${label}</span>`
      : '';
  }

  renderPriceLabel(label, labelColorClass) {
    const { $priceLabel } = this.viewModel;

    if ($priceLabel) {
      $priceLabel.innerHTML = label
        ? `<span class="c-product-tile__price-label-content fixed-utility-s ${labelColorClass}">${label}</span>`
        : '';
    }
  }

  initialCardBuildCleanUp() {
    const { $infoFooter } = this.viewModel;

    this.scope.classList.remove('product-card-custom--info-padding');
    $infoFooter.classList.remove('hidden');
  }

  createSwatchOverflowItem(remaining) {
    const span = document.createElement('span');
    span.className = 'c-product-tile__swatch-image--more fixed-utility-s';
    span.textContent = `+${remaining}`;
    return span;
  }
}

class ProductSwatchController {
  constructor({ root, viewModel, data, onVariantSelect }) {
    this.root = root;
    this.viewModel = viewModel.vm;
    this.productData = data;

    this.initialId = this.productData.id;
    this.onVariantSelect = onVariantSelect;
    this.swatchButtons = [];
    this.dataStore = {};
    this.handleSwatchClickBound = this.handleSwatchClick.bind(this);
    this.observer = null;
  }

  async initialize() {
    this.swatchButtons = this.viewModel.$swatchList?.querySelectorAll(`.${PRODUCT_SELECTORS.SWATCH_CLASS}`);
    const productIds = this.extractProductIDs();
    this.saveInitialProductData();

    if (productIds.length > 0) {
      const metadata = await this.fetchProductMetadata(productIds);
      const unifiedData = this.tranformMetadata(metadata);

      this.dataStore = {
        ...this.dataStore,
        ...unifiedData,
      };

      this.attachSwatchListeners();
    }

    this.processSwatches();
  }

  saveInitialProductData() {
    // Swatches needed only for the initial ProductCardDOMPatcher render
    delete this.productData.swatches;
    this.dataStore[this.initialId] = this.productData;
  }

  tranformMetadata(metadata) {
    return Object.entries(metadata).reduce((acc, [id, product]) => {
      const transformed = cardHelpers.transformProductMetadataToCardData(product);
      if (transformed) {
        acc[id] = transformed;
      }
      return acc;
    }, {});
  }

  async fetchProductMetadata(productIds) {
    const result = await fetchProductsBatched(productIds);

    const resultConverted = Object.fromEntries(Array.from(result.byId.entries()));

    return resultConverted;
  }

  extractProductIDs() {
    return this.productData?.swatches?.map((cp) => cp.productID).filter((id) => id !== this.initialId) || [];
  }

  processSwatches() {
    const swatchStates = [];
    let checkedSwatch = null;

    this.swatchButtons.forEach((swatch) => {
      const productId = swatch.getAttribute('data-product-id');
      const productData = this.dataStore[productId];

      if (swatch.classList.contains('is-checked')) checkedSwatch = swatch;

      if (productData) {
        const isAvailable = cardHelpers.isInStock(productData);

        swatch.setAttribute('data-variant-handle', productData.handle);

        if (!isAvailable) {
          this.markUnavailable(swatch);
        }

        swatchStates.push({ swatch, isAvailable });
      }
    });

    const availableSwatches = swatchStates.filter((s) => s.isAvailable);
    const visibleSwatches = availableSwatches.length > 0 ? availableSwatches : [];
    const maxVisible = 4;

    visibleSwatches.slice(0, maxVisible).forEach(({ swatch }) => this.setSwatchVisible(swatch));

    const remaining = visibleSwatches.length - maxVisible;

    if (remaining > 0) {
      const moreItem = this.root.domPatcher.createSwatchOverflowItem(remaining);
      this.viewModel.$swatchList.appendChild(moreItem);
    }

    // Show the first unavailable swatch if nothing available
    if (availableSwatches.length === 0) {
      this.setSwatchVisible(checkedSwatch);
    }
  }

  setSwatchVisible(swatch) {
    swatch?.closest(`.${PRODUCT_SELECTORS.COLOR_VALUE_WRAPPER}`)?.classList?.remove('hidden');
  }

  attachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClickBound);
    });
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    const productId = swatch.getAttribute('data-product-id');
    const productData = this.dataStore[productId];

    if (!productData) return;

    if (!swatch.classList.contains('is-checked')) {
      this.updateSwatchSelection(swatch);
      this.onVariantSelect?.(productData);
    }
  }

  updateSwatchSelection(currentSwatch) {
    this.swatchButtons.forEach((btn) => btn.classList.remove('is-checked'));
    currentSwatch.classList.add('is-checked');
  }

  markUnavailable(swatch) {
    swatch.classList.add('is-unavailable');
  }

  detachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.removeEventListener('click', this.handleSwatchClickBound);
    });
  }
}

class ProductCardComponent extends HTMLElement {
  connectedCallback() {
    const jsonAttr = this.getAttribute('data-json');
    const jsonScript = this.querySelector('script[type="application/json"]');

    if (!this.querySelector('.c-product-tile')) {
      const skeleton = document.querySelector('#product-card-placeholder');
      if (skeleton) {
        const html = skeleton.innerHTML;
        this.innerHTML = html;
      }
    }

    if (this._productData) {
      this.setup(this._productData);
      return;
    }

    if (jsonAttr) {
      try {
        this.productData = JSON.parse(jsonAttr);
        return;
      } catch (e) {
        console.warn('Invalid JSON in data-json attribute', e);
      }
    }

    if (jsonScript) {
      try {
        this.productData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.warn('Invalid JSON in <script>', e);
      }
    }
  }

  set productData(data) {
    this._productData = data;
    this.setup(data);
  }

  get productData() {
    return this._productData;
  }

  async setup(data) {
    this.viewModel = new ProductCardViewModel(this);
    this.domPatcher = new ProductCardDOMPatcher(this);
    this.isSwatchesInitialized = false;
    this._idleHandle = null;
    this._hydratedOnce = this._hydratedOnce || false;
    this.swatchController = new ProductSwatchController({
      root: this,
      viewModel: this.viewModel,
      data,
      onVariantSelect: (productData) => {
        // Smooth updates on variant changes without heavy transitions
        this.smoothSwapBegin({ lockHeight: false });
        this.domPatcher.render(productData);
        this.smoothSwapCommit();
      },
    });

    // Smooth initial hydration only once per element
    this.smoothSwapBegin({ lockHeight: true });
    this.domPatcher.render(data);
    this.domPatcher.initialCardBuildCleanUp();
    this.smoothSwapCommit();

    this.safeInitializeSwatches = async () => {
      if (this.isSwatchesInitialized) return;
      this.isSwatchesInitialized = true;
      await this.swatchController.initialize();
    };

    // Normalize attribute name to lowercase variant
    if (this.getAttribute('data-enteredviewport') === 'true') {
      await this.safeInitializeSwatches();
    }

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName &&
          mutation.attributeName.toLowerCase() === 'data-enteredviewport'
        ) {
          if (this.getAttribute('data-enteredviewport') === 'true') {
            this.safeInitializeSwatches();
          }
        }
      }
    });
    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-enteredviewport'],
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._idleHandle && window.cancelIdleCallback) window.cancelIdleCallback(this._idleHandle);
    this.swatchController.detachSwatchListeners();
  }

  smoothSwapBegin({ lockHeight }) {
    // Respect reduced motion
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    // Initial hydration: do once; variant updates: allow lightweight fade
    const card = this.querySelector('.c-product-tile');
    if (!card) return;

    if (lockHeight && !this._hydratedOnce) {
      this.style.minHeight = `${this.offsetHeight || card.offsetHeight || 0}px`;
    }

    // Prepare fade-in
    card.style.willChange = 'opacity';
    card.style.opacity = '0.001';
  }

  smoothSwapCommit() {
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const card = this.querySelector('.c-product-tile');
    if (!card) return;

    const finalize = () => {
      this._hydratedOnce = true;
      this.style.minHeight = '';
      card.style.willChange = '';
      card.style.transition = '';
    };

    // Double rAF to ensure styles are applied before transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        card.style.transition = 'opacity 180ms ease-out';
        card.style.opacity = '1';
        const onEnd = () => {
          card.removeEventListener('transitionend', onEnd);
          finalize();
        };
        card.addEventListener('transitionend', onEnd, { once: true });
        // Safety timeout in case transitionend doesn't fire
        setTimeout(finalize, 250);
      });
    });
  }
}

customElements.define('product-card', ProductCardComponent);
